import React from 'react';
import { connect } from 'react-redux';
import Avatar from '@material-ui/core/Avatar';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardActions from '@material-ui/core/CardActions';
import CardContent from '@material-ui/core/CardContent';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormGroup from '@material-ui/core/FormGroup';
import FormLabel from '@material-ui/core/FormLabel';
import Input from '@material-ui/core/Input';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import CircularProgress from '@material-ui/core/CircularProgress';
import Checkbox from '@material-ui/core/Checkbox';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { withStyles, WithStyles } from '@material-ui/core/styles';
import { withTranslation, WithTranslation } from 'react-i18next';
import { Link as RouterLink, match } from 'react-router-dom';

// Components
import { LCAlert } from '../../../components/alert';
import { Anchor } from '../../../components/anchor';
import { AnchorBlock } from '../../../components/anchorBlock';
import App from '../../../components/app';
import { ActionCustom, ActionFunction, FormatType, ListCell } from '../../../local-types';
import { Dialog } from '../../../components/dialog';
import ErrorMessage from '../../../components/error-message';
import EnhancedListItem from '../../../components/listItem';
import MyLink from '../../../components/mylink';
import { Typobold } from '../../../components/typobold';
import { ActionLogs } from '../../../components/action-logs';
import { SentMessages } from '../../../components/sent-messages';
import { InlineDocument } from '../../../components/inlineDocument';
import FormSubmit from '../../../components/form/formSubmit';
import { Comment } from '../../../components/comment';
// Modules
import { actions as errorActions, selectors as errorsSelectors, ErrorPayload } from '../../../redux/reducers/errors';
import {
    Driver,
    DocumentType,
    DocType,
    DriverPoints,
    DriverPointOperation,
    TParams,
} from '../../../redux/reducers/api-types';
import {
    actions as driverActions,
    selectors as driverSelectors,
    types as driverTypes,
    DriverById,
    DispatchGroup,
    DRIVER_POINTS_PER_PAGE,
} from '../../../redux/reducers/drivers';
import { actions as partnerActions } from '../../../redux/reducers/partners';
import { selectors as pendingSelectors } from '../../../redux/reducers/pendingOperations';
// Ressources
import DateUtils from '../../../utils/dateUtils';
import FormattingUtils from '../../../utils/formattingUtils';
import FormUtils from '../../../utils/forms';
import ObjectUtils from '../../../utils/objectUtils';
import UserUtils, { AccountRight, RightRank } from '../../../utils/userUtils';
import StringUtils from '../../../utils/stringUtils';
import { paths, createPath } from '../../paths';
import { EntityName } from '../../../redux/reducers/generic';
import styles from './driver-view-style';
import { getBanReason } from '../../../utils/businessUtils';
import { clsx } from '../../../utils/htmlUtils';
import withGlobalStyles from '../../../utils/global-styles';

interface DriverViewPageProps extends WithStyles, WithTranslation {
    globalClasses: any;
    match: match<TParams>;
    drivers: DriverById;
    driverPoints: { [driverId: string]: DriverPoints };
    dispatchGroups: DispatchGroup[];
    getDriver: (id: string) => any;
    getDriverPoints: (id: string, page?: number) => any;
    getDispatchGroups: () => any;
    editDriver: (id: string, payload, callback?) => any;
    resetDriverPassword: (id, callback?) => any;
    suspendDriver: (id, reason, date, callback?) => any;
    unsuspendDriver: (id) => any;
    saveAdjustment: (payload: any) => any;
    clearError: (errorType: string) => void;
    resetDriverPwdError: ErrorPayload;
    editDriverError: ErrorPayload;
    getDriverError: ErrorPayload;
    getDriverPointsError: ErrorPayload;
    getDispatchGroupsError: ErrorPayload;
    suspendDriverError: ErrorPayload;
    unsuspendDriverError: ErrorPayload;
    saveAdjustmentError: ErrorPayload;
    isSuspendingOrUnsuspending: boolean;
    isLoadingDriverPoints: boolean;
    isEditingDriver: boolean;
}

interface DriverViewPageState {
    formData: any;
    // Last values known to be on the server: the validate button lights up when the form differs from them.
    saved: { banned: boolean; dispatch_groups: string[] };
    suspend: boolean;
}

class DriverViewPage extends React.Component<DriverViewPageProps, DriverViewPageState> {
    alertRef: any;
    savedAlertRef: any;
    isPartnerSetting: boolean = UserUtils.checkRights({
        name: AccountRight.PARTNER_SETTINGS,
        mode: RightRank.W,
    });
    isPartnerBillingWrite: boolean = UserUtils.checkRights({
        name: AccountRight.PARTNER_BILLING,
        mode: RightRank.W,
    });
    isDriverPrebookRead: boolean = UserUtils.checkRights({
        name: AccountRight.DRIVER_PREBOOK,
        mode: RightRank.R,
    });
    isDriverPrebookWrite: boolean = UserUtils.checkRights({
        name: AccountRight.DRIVER_PREBOOK,
        mode: RightRank.W,
    });

    constructor(props: DriverViewPageProps) {
        super(props);
        this.state = {
            formData: {},
            saved: { banned: false, dispatch_groups: [] },
            suspend: false,
        };
    }

    static getDerivedStateFromProps(props, state) {
        const id = props.match.params.id;
        const payload = props.drivers[id];
        if (payload && !state.formData.id) {
            const tmp = {};
            const names = [];
            const next = { ...payload };
            next.dispatch_groups
                .filter((dg) => dg.id === 'normal')
                .forEach((element) => {
                    tmp[element.id] = true;
                    names.push(element.name);
                });
            next.name_dispatch_groups = names;
            next.the_dispatch_groups = tmp;
            next.dispatch_groups = Object.keys(tmp);

            let value = next.banned_until_date;
            if (value && typeof value === 'number') {
                value = DateUtils.dateToString(new Date(value * 1000));
                next.banned_until_date = value;
            }
            return { formData: next, saved: { banned: !!next.banned, dispatch_groups: [...next.dispatch_groups] } };
        }
        return null;
    }

    componentDidMount() {
        this.load();
    }

    load = (): void => {
        // A failure recorded for the entity we come from would otherwise greet the next
        // one with the retry panel instead of the loader.
        this.props.clearError(driverTypes.GET_DRIVER);

        const id = this.props.match.params.id;
        this.props.getDriver(id);
        this.props.getDriverPoints(id);

        if (this.props.dispatchGroups.length === 0) {
            this.props.getDispatchGroups();
        }
    };

    //=============================================//
    //=============== Event Handler ===============//
    //=============================================//

    handleSubmit = (event): void => {
        event.preventDefault();
        const { match } = this.props;
        const id = match.params.id;

        const { dispatch_groups, the_dispatch_groups, banned, ban_description, banned_until_date } =
            this.state.formData;

        const payload = {
            dispatch_groups: dispatch_groups.length > 0 ? dispatch_groups : '',
            the_dispatch_groups,
            banned,
            ban_description: banned ? ban_description : null,
            banned_until_date: banned ? banned_until_date : null,
        };

        this.savedAlertRef?.closeAlert();
        this.props.editDriver(id, payload, () => {
            this.setState({ saved: { banned: !!banned, dispatch_groups: [...dispatch_groups] } });
            this.savedAlertRef?.showAlert();
            // Refreshes the header bubble and the history with what the server recorded.
            this.props.getDriver(id);
        });
    };

    isAdditionalInfosDirty = (): boolean => {
        const { formData, saved } = this.state;
        return (
            !!formData.banned !== saved.banned || !ObjectUtils.isEqual(formData.dispatch_groups, saved.dispatch_groups)
        );
    };

    handleAction(driver: Driver, fields): void {
        const payload = {
            label: fields.description,
            partner_id: driver.partner.uuid,
            pre_tax_partner: Number(fields.amount),
            product_type: 'adjustment_transport',
        };
        // console.log(payload)
        this.props.saveAdjustment(payload);
    }

    handleChange = (event): void => {
        this.setState(FormUtils.nextSubState(event, this.state, 'formData'));
    };

    handleUnsuspend = (event): void => {
        const { match, unsuspendDriver } = this.props;
        const id = match.params.id;
        unsuspendDriver(id);
    };

    handleSuspend = (event): void => {
        this.setState({ formData: this.state.formData, suspend: !this.state.suspend });
    };

    handleSavePenalty = (): void => {
        const { match, suspendDriver } = this.props;
        const id = match.params.id;
        const { penalty_description, penalty_until_date } = this.state.formData;
        suspendDriver(id, penalty_description, penalty_until_date, () => {
            this.setState({ formData: this.state.formData, suspend: false });
        });
    };

    renderActions = (): (ActionCustom | ActionFunction)[] => {
        const { t, classes, match, drivers } = this.props;

        const id = match.params.id;
        const driverPayload = drivers[id];

        const actions: (ActionCustom | ActionFunction)[] = [];

        if (this.isPartnerBillingWrite) {
            actions.push({
                label: t('booking:refound.btnIncome'),
                customView: (
                    <Dialog
                        btnTitle={t('booking:refound.title')}
                        description={t('booking:refound.description')}
                        customButton={<div>{t('booking:refound.btnIncome')}</div>}
                        formComponent={
                            <>
                                <FormControl key={'formReason'} style={{ display: 'flex' }}>
                                    <div>{t('booking:refound.reason')}</div>
                                    <Input
                                        type="text"
                                        name={'description'}
                                        className={classes.formInput}
                                        required={true}
                                        inputProps={{ list: 'reasons' }}
                                    />
                                    <datalist id={'reasons'}>
                                        <option value={t('booking:refound.optionsReason.a') as string} />
                                        <option value={t('booking:refound.optionsReason.b') as string} />
                                        <option value={t('booking:refound.optionsReason.c') as string} />
                                        <option value={t('booking:refound.optionsReason.d') as string} />
                                        <option value={t('booking:refound.optionsReason.e') as string} />
                                    </datalist>
                                </FormControl>
                                <br />
                                <FormControl key={'formAmount'}>
                                    <div>{t('booking:refound.adjustment_empty')}</div>
                                    <Input
                                        type="number"
                                        name={'amount'}
                                        className={classes.formInput}
                                        required={true}
                                        inputProps={{ step: 0.01 }}
                                    />
                                    <br />
                                    <div>{t('booking:refound.amount_hint')}</div>
                                </FormControl>
                            </>
                        }
                        onOK={(fields) => this.handleAction(driverPayload, fields)}
                    />
                ),
            });
        }
        if (this.isPartnerSetting) {
            actions.push({
                label: t('common:reset_password'),
                func: () =>
                    this.props.resetDriverPassword(id, () => {
                        this.alertRef.showAlert();
                    }),
            });
        }
        return actions;
    };

    /** Total line, then the B2C / B2B split, for the week or month driver stats. */
    formatStats = (driverPayload: Driver, period: 'week' | 'month'): string => {
        const { t } = this.props;
        const segments = (['b2c', 'b2b'] as const).map((segment) => ({
            segment,
            count: driverPayload[`nb_bookings_${period}_${segment}` as const] ?? 0,
            revenue: driverPayload[`total_net_price_without_vat_${period}_${segment}` as const] ?? 0,
        }));
        const line = (count: number, revenue: number) =>
            t('stats.info', {
                nbCourse: count,
                CA: FormattingUtils.price(revenue, true),
                avgCA: FormattingUtils.price(count > 0 ? revenue / count : 0, true),
            });

        const total = line(
            segments.reduce((sum, s) => sum + s.count, 0),
            segments.reduce((sum, s) => sum + s.revenue, 0),
        );
        const details = segments.map((s) => `${t(`stats.${s.segment}`)} : ${line(s.count, s.revenue)}`);
        return [total, ...details].join('\n');
    };

    listItemBuilder = (driverPayload: Driver): ListCell[] => {
        const { t } = this.props;
        const { last_session } = driverPayload;

        const listItem: ListCell[] = [
            { title: t('common:form.id'), data: driverPayload.id, readOnly: true },
            { title: t('common:form.firstname'), data: driverPayload.firstname },
            { title: t('common:form.lastname'), data: driverPayload.lastname },
            { title: t('common:form.email'), data: driverPayload.email },
            {
                title: t('control_tower'),
                data: driverPayload.details?.controlTower?.name || 'N/A',
            },
            {
                title: t('tpe'),
                data: driverPayload.tpe,
                type: FormatType.Boolean,
                optional: true,
            },
            {
                title: t('vtc_number'),
                data: driverPayload.licence_number_atout_france,
            },
            {
                title: t('licence_number'),
                data: driverPayload.driving_licence_number,
            },
            {
                title: t('common:form.phone_number'),
                data: driverPayload.phone_number,
                type: FormatType.Phone,
                copyable: true,
            },
            {
                title: t('stats.week'),
                readOnly: true,
                data: this.formatStats(driverPayload, 'week'),
            },
            {
                title: t('stats.month'),
                readOnly: true,
                data: this.formatStats(driverPayload, 'month'),
            },
            {
                title: t('common:form.partner'),
                readOnly: true,
                data: (
                    <MyLink color="secondary" to={`${paths.partners}/${driverPayload.partner.id}`}>
                        {driverPayload.partner.name}
                    </MyLink>
                ),
            },
            {
                title: t('courses'),
                readOnly: true,
                data:
                    driverPayload.nb_bookings > 0 ? (
                        <MyLink color="secondary" to={`${paths.bookings}?filters[driver.id]=${driverPayload.id}`}>{`${
                            driverPayload.nb_bookings
                        } (${t('common:click_to_see')})`}</MyLink>
                    ) : (
                        driverPayload.nb_bookings
                    ),
                optional: true,
            },
            {
                title: t('rating'),
                readOnly: true,
                data: driverPayload.rating ? `${driverPayload.rating.toFixed(2)} / 5` : '',
                optional: true,
            },
            {
                title: t('rated_courses'),
                readOnly: true,
                data: driverPayload.rating && (
                    <MyLink
                        color="secondary"
                        to={`${paths.bookings}?filters[driver.id]=${driverPayload.id}&filters[rated]=1`}
                    >
                        {t('rated_courses_link')}
                    </MyLink>
                ),
                optional: true,
            },
            {
                title: t('creation_date'),
                readOnly: true,
                data: driverPayload.subscription_date,
                type: FormatType.DateLong,
            },
            {
                title: t('last_session'),
                readOnly: true,
                data:
                    last_session &&
                    `${DateUtils.tsToDate(last_session.sign_in)}\nApp v${last_session.app_version}\n${
                        last_session.device
                            ? `${StringUtils.upperFirst(last_session.device.model_name) ?? ''} ${
                                  last_session.device.type ?? ''
                              } ${last_session.device.os_version ?? ''}`
                            : ''
                    }`,
                optional: true,
            },
            {
                title: t('common:form.additional_infos'),
                data: driverPayload.additional_info,
                optional: true,
            },
        ];

        if (!this.isPartnerSetting) {
            listItem.pop(); // Remove rated_courses
            listItem.push({
                title: t('common:suspended'),
                data: driverPayload?.banned ? (
                    <div>
                        {FormattingUtils.format(driverPayload.banned, FormatType.Boolean, t)}
                        {' - '}
                        {t('ban_reason.title')}
                        {' : '}
                        {getBanReason(t, driverPayload)}{' '}
                        {driverPayload.banned_until_date &&
                            t('ban_until', {
                                date: FormattingUtils.format(driverPayload.banned_until_date, FormatType.DateShort, t),
                            })}
                    </div>
                ) : (
                    <div>{FormattingUtils.format(driverPayload.banned, FormatType.Boolean, t)}</div>
                ),
            });
            if (driverPayload.name_dispatch_groups) {
                listItem.push({
                    title: t('can_be_berline'),
                    data: driverPayload.name_dispatch_groups.length > 0 && (
                        <List>
                            {driverPayload.name_dispatch_groups.map((d) => (
                                <ListItem key={d}> - {d}</ListItem>
                            ))}
                        </List>
                    ),
                });
            }
        }

        return listItem;
    };

    listItemPrebookBuilder = (driverPayload: Driver): ListCell[] => {
        const { t } = this.props;
        const { details } = driverPayload;
        const listItem: ListCell[] = [
            { title: t('common:form.lastname'), data: details?.prebookGroup?.name },
            {
                title: t('prebook.title'),
                data:
                    details?.prebookSuspendedUntilDate &&
                    details.prebookSuspendedUntilDate * 1000 > new Date().getTime()
                        ? t('prebook.penalty', {
                              time: DateUtils.getFullDiff(
                                  t,
                                  DateUtils.dateDiff(new Date(), new Date(details.prebookSuspendedUntilDate * 1000)),
                              ),
                          })
                        : t('prebook.no_penalty'),
            },
        ];
        return listItem;
    };

    handleLoadMorePoints = (): void => {
        const { match, driverPoints, getDriverPoints } = this.props;
        const id = match.params.id;
        const points = driverPoints[id];
        getDriverPoints(id, (points?.page ?? 0) + 1);
    };

    /** Ride the operation was triggered by, when it is still reachable in the BO. */
    renderPointOperationSource = (operation: DriverPointOperation): JSX.Element | string => {
        const { t } = this.props;
        const { entity_type, entity_id } = operation;
        if (!entity_type) {
            return '';
        }
        const label = t(`points.entity.${entity_type}`, { defaultValue: entity_type }) as string;
        if (!entity_id) {
            return label;
        }
        const path = entity_type === 'reservation' ? paths.reservations : paths.bookings;
        return (
            <MyLink color="secondary" to={`${path}/${entity_id}`}>
                {`${label} ${entity_id}`}
            </MyLink>
        );
    };

    renderPointOperations = (operations: DriverPointOperation[]): JSX.Element[] => {
        const { t, classes } = this.props;
        return operations.map((operation) => (
            <TableRow key={operation.uuid}>
                <TableCell component="th" scope="row">
                    {DateUtils.tsToDate(operation.created_at)}
                </TableCell>
                <TableCell>
                    {t(`points.event.${operation.event}`, { defaultValue: operation.event })}
                    {operation.label ? ` - ${operation.label}` : ''}
                </TableCell>
                <TableCell className={operation.amount < 0 ? classes.pointsSpent : classes.pointsGained}>
                    {operation.amount > 0 ? `+${operation.amount}` : operation.amount}
                </TableCell>
                <TableCell>{this.renderPointOperationSource(operation)}</TableCell>
            </TableRow>
        ));
    };

    renderPoints = (points: DriverPoints): JSX.Element => {
        const { t, classes, isLoadingDriverPoints } = this.props;
        const hasMore = points.operations.length < points.nb_operations;

        return (
            <>
                <div className={classes.pointsSummary}>
                    <div>
                        {`${t('points.balance')} : `}
                        <b>{points.total}</b>
                    </div>
                    <div className={classes.pointsGained}>{`${t('points.gained')} : +${points.gained}`}</div>
                    <div className={classes.pointsSpent}>{`${t('points.spent')} : -${points.spent}`}</div>
                </div>
                {points.operations.length > 0 && (
                    <Table className={classes.table} size="small" aria-label="Table points">
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('points.date')}</TableCell>
                                <TableCell>{t('points.reason')}</TableCell>
                                <TableCell>{t('points.amount')}</TableCell>
                                <TableCell>{t('points.source')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>{this.renderPointOperations(points.operations)}</TableBody>
                    </Table>
                )}
                {hasMore && (
                    <div className={classes.pointsMore}>
                        <Button color="secondary" onClick={this.handleLoadMorePoints} disabled={isLoadingDriverPoints}>
                            {t('points.load_more', { nb: DRIVER_POINTS_PER_PAGE })}
                        </Button>
                    </div>
                )}
            </>
        );
    };

    listDocumentsBuilder = (driverPayload: Driver): ListCell[] => {
        const { t } = this.props;
        const { documents } = driverPayload;

        const listDocuments: DocumentType[] = [
            {
                id: documents.driver_license,
                name: t('common:docs.driver_license'),
                docType: DocType.DriverLicense,
                // lastControlDate: true,
                controlDate: true,
                endDate: true,
                maxDate: 15, // +15 ans from creation date
            },
            {
                id: documents.vtc_card,
                name: t('common:docs.vtc_card'),
                docType: DocType.VtcCard,
                // lastControlDate: true,
                controlDate: true,
                endDate: true,
                maxDate: 25,
            },
            {
                id: documents.adp,
                name: t('common:docs.adp'),
                docType: DocType.Adp,
                endDate: true,
            },
            {
                id: documents.driver_license_statement,
                name: t('common:docs.driver_license_statement'),
                docType: DocType.DriverLicenseStatement,
                endDate: !!documents.driver_license_statement,
                startDate: true,
                startLabel: t('date_driver_license_statement'),
            },
        ];

        return listDocuments.map((doc) => {
            return {
                title: t(`common:docs.${doc.docType}`),
                data: true,
                customValue: (
                    <InlineDocument
                        {...doc}
                        docClass="driver"
                        readOnly={!this.isPartnerSetting}
                        classId={driverPayload.id}
                        noLabel={true}
                    />
                ),
            };
        });
    };

    renderCourseTypeView = (driverPayload: Driver, dispatchGroups: DispatchGroup[]): JSX.Element[] => {
        const { formData } = this.state;
        if (!driverPayload || !dispatchGroups) {
            return null;
        }

        return dispatchGroups
            .filter((dg) => dg.id === 'normal')
            .map((item) => (
                <FormControlLabel
                    key={item.id}
                    style={{ marginLeft: 8 }}
                    control={
                        <Checkbox
                            checked={formData.dispatch_groups.findIndex((g) => g === item.id) !== -1}
                            value={item.id}
                            onChange={() => {
                                const checked = formData.dispatch_groups.includes(item.id);
                                this.setState({
                                    formData: {
                                        ...formData,
                                        dispatch_groups: checked
                                            ? formData.dispatch_groups.filter((g) => g !== item.id)
                                            : [...formData.dispatch_groups, item.id],
                                        the_dispatch_groups: { ...formData.the_dispatch_groups, [item.id]: !checked },
                                    },
                                });
                            }}
                        />
                    }
                    label={item.name}
                />
            ));
    };

    render() {
        const { formData, suspend } = this.state;
        const {
            t,
            globalClasses,
            classes,
            match,
            dispatchGroups,
            drivers,
            driverPoints,
            resetDriverPwdError,
            editDriverError,
            getDriverError,
            getDriverPointsError,
            getDispatchGroupsError,
            suspendDriverError,
            unsuspendDriverError,
            saveAdjustmentError,
            isSuspendingOrUnsuspending,
            isLoadingDriverPoints,
            isEditingDriver,
        } = this.props;
        const id = match.params.id;
        const driverPayload = drivers[id];
        const points = driverPoints[id];
        const hasPenalty =
            driverPayload?.details?.prebookSuspendedUntilDate &&
            driverPayload.details.prebookSuspendedUntilDate * 1000 > new Date().getTime();

        return (
            <App
                needRights={{ name: AccountRight.PARTNER_SETTINGS, mode: RightRank.R }}
                loading={!driverPayload}
                loadingError={getDriverError}
                onRetry={this.load}
                header={{
                    icon: 'default',
                    title: driverPayload?.fullname,
                    subtitle: driverPayload && `ID: ${driverPayload.id}`,
                    bubble: {
                        visible: driverPayload?.banned || hasPenalty,
                        text: driverPayload?.banned ? t('common:suspended') : t('common:penalised'),
                    },
                    actions: this.renderActions(),
                    anchors: [
                        { href: '#general_infos', label: t('common:form.general_infos') },
                        {
                            href: '#additional_infos',
                            label: t('common:form.additional_infos'),
                        },
                        { href: '#points', label: t('points.title') },
                        { href: '#documents', label: t('common:form.documents') },
                        { href: '#sent_messages', label: t('sent_messages:title') },
                    ],
                }}
            >
                <ErrorMessage payload={getDriverError || editDriverError} />
                <ErrorMessage payload={resetDriverPwdError} />
                <LCAlert
                    onRef={(ref) => (this.alertRef = ref)}
                    details={t('login:forgot_password_sent')}
                    type="success"
                />
                <ErrorMessage payload={getDispatchGroupsError} />
                <ErrorMessage payload={saveAdjustmentError} />
                {driverPayload && (
                    <>
                        <Anchor id="general_infos" />
                        {driverPayload.photo_url && (
                            <Avatar alt="Avatar" src={driverPayload.photo_url} className={classes.bigAvatar} />
                        )}
                        <EnhancedListItem
                            datas={this.listItemBuilder(driverPayload)}
                            title={t('common:form.general_infos')}
                            cardActions={
                                this.isPartnerSetting && (
                                    <Button
                                        color="secondary"
                                        variant="contained"
                                        component={RouterLink}
                                        to={createPath(paths.driversEdit, { id })}
                                    >
                                        {t('common:edit')}
                                    </Button>
                                )
                            }
                        />
                        <br />

                        <Anchor id="comment" />
                        <Comment entity={EntityName.DRIVERS} entityId={id} />
                        <br />

                        {this.isPartnerSetting && formData.id && (
                            <FormSubmit debounceSubmit={{ time: 500, callback: this.handleSubmit }}>
                                <>
                                    <Anchor id="additional_infos" />
                                    <Typobold>{t('common:form.additional_infos')}</Typobold>
                                    <LCAlert
                                        onRef={(ref) => (this.savedAlertRef = ref)}
                                        details={t('additional_infos_saved')}
                                        type="success"
                                    />
                                    <Card className={clsx(classes.card, classes.partnerInfo)}>
                                        <CardContent>
                                            <List>
                                                <ListItem>
                                                    <FormLabel className={classes.partnerInfoLabel}>
                                                        {t('common:suspended')}
                                                    </FormLabel>
                                                    <FormControl margin="dense">
                                                        <Checkbox
                                                            style={{ marginLeft: 8 }}
                                                            edge="start"
                                                            name={'banned'}
                                                            checked={formData.banned}
                                                            onChange={this.handleChange}
                                                            tabIndex={-1}
                                                        />
                                                    </FormControl>
                                                    {driverPayload.banned && (
                                                        <div>
                                                            {`${t('ban_reason.title')} : ${getBanReason(t, driverPayload)}`}
                                                        </div>
                                                    )}

                                                    {formData.banned && (
                                                        <>
                                                            <FormLabel
                                                                style={{ marginLeft: 16 }}
                                                                className={classes.partnerInfoLabel}
                                                            >
                                                                {`${t('ban_description.title')} *`}
                                                            </FormLabel>
                                                            <FormControl margin="dense" style={{ flex: 1 }}>
                                                                <FormGroup row={true}>
                                                                    <Input
                                                                        style={{ marginLeft: 8, flex: 1 }}
                                                                        id={'ban_description'}
                                                                        type={'text'}
                                                                        required={true}
                                                                        placeholder={t('ban_description.placeholder')}
                                                                        name={'ban_description'}
                                                                        value={formData.ban_description || ''}
                                                                        className={classes.formInput}
                                                                        onChange={this.handleChange}
                                                                    />
                                                                </FormGroup>
                                                            </FormControl>
                                                            <FormLabel
                                                                style={{ marginLeft: 16 }}
                                                                className={classes.partnerInfoLabel}
                                                            >
                                                                {t('ban_temporary')}
                                                            </FormLabel>
                                                            <FormControl margin="dense">
                                                                <FormGroup row={true}>
                                                                    <Input
                                                                        style={{ marginLeft: 8 }}
                                                                        id={'banned_until_date'}
                                                                        type={'date'}
                                                                        name={'banned_until_date'}
                                                                        value={formData.banned_until_date || ''}
                                                                        className={classes.formInput}
                                                                        onChange={this.handleChange}
                                                                    />
                                                                </FormGroup>
                                                            </FormControl>
                                                        </>
                                                    )}
                                                </ListItem>
                                                <ListItem>
                                                    <FormLabel className={classes.partnerInfoLabel}>
                                                        {t('can_be_berline')}
                                                    </FormLabel>
                                                    <FormControl margin="dense">
                                                        <FormGroup row={true}>
                                                            {this.renderCourseTypeView(driverPayload, dispatchGroups)}
                                                        </FormGroup>
                                                    </FormControl>
                                                </ListItem>
                                            </List>
                                        </CardContent>
                                        <CardActions className={classes.rightActions}>
                                            <Button
                                                type="submit"
                                                variant="contained"
                                                disabled={isEditingDriver}
                                                color={this.isAdditionalInfosDirty() ? 'secondary' : 'inherit'}
                                                startIcon={isEditingDriver && <CircularProgress size={16} />}
                                            >
                                                {t('common:validate')}
                                            </Button>
                                        </CardActions>
                                    </Card>
                                </>
                            </FormSubmit>
                        )}
                        {this.isDriverPrebookRead && (
                            <>
                                <br />
                                <ErrorMessage payload={unsuspendDriverError || suspendDriverError} />
                                <EnhancedListItem
                                    datas={this.listItemPrebookBuilder(driverPayload)}
                                    title={t('prebook:title')}
                                    loader={
                                        isSuspendingOrUnsuspending ? (
                                            <CircularProgress
                                                size={25}
                                                style={{ display: 'flex', flex: 1, justifyContent: 'center' }}
                                            />
                                        ) : (
                                            <FormSubmit
                                                id="penaltyForm"
                                                debounceSubmit={{ time: 500, callback: this.handleSavePenalty }}
                                                className={globalClasses.centerVertical}
                                            >
                                                {suspend && (
                                                    <>
                                                        <FormLabel
                                                            style={{ marginLeft: 16 }}
                                                            className={classes.partnerInfoLabel}
                                                        >
                                                            {`${t('ban_description.title')} *`}
                                                        </FormLabel>
                                                        <FormControl margin="dense" style={{ flex: 1 }}>
                                                            <FormGroup row={true}>
                                                                <Input
                                                                    style={{ marginLeft: 8, flex: 1 }}
                                                                    id={'penalty_description'}
                                                                    type={'text'}
                                                                    required={true}
                                                                    placeholder={t('ban_description.placeholder')}
                                                                    name={'penalty_description'}
                                                                    value={formData.penalty_description || ''}
                                                                    className={classes.formInput}
                                                                    onChange={this.handleChange}
                                                                />
                                                            </FormGroup>
                                                        </FormControl>
                                                        <FormLabel
                                                            style={{ marginLeft: 16 }}
                                                            className={classes.partnerInfoLabel}
                                                        >
                                                            {`${t('penalty_temporary')} *`}
                                                        </FormLabel>
                                                        <FormControl margin="dense">
                                                            <FormGroup row={true}>
                                                                <Input
                                                                    style={{ marginLeft: 8 }}
                                                                    id={'penalty_until_date'}
                                                                    type={'date'}
                                                                    name={'penalty_until_date'}
                                                                    required={true}
                                                                    value={formData.penalty_until_date || ''}
                                                                    className={classes.formInput}
                                                                    onChange={this.handleChange}
                                                                />
                                                            </FormGroup>
                                                        </FormControl>
                                                    </>
                                                )}
                                            </FormSubmit>
                                        )
                                    }
                                    cardActions={
                                        this.isDriverPrebookWrite && (
                                            <>
                                                <Button
                                                    color="primary"
                                                    variant="contained"
                                                    onClick={hasPenalty ? this.handleUnsuspend : this.handleSuspend}
                                                >
                                                    {hasPenalty
                                                        ? t('prebook.unsuspend')
                                                        : suspend
                                                          ? t('prebook.cancelPenalty')
                                                          : t('prebook.suspend')}
                                                </Button>
                                                {suspend && (
                                                    <Button
                                                        type="submit"
                                                        color="secondary"
                                                        variant="contained"
                                                        form="penaltyForm"
                                                        // onClick={this.handleSavePenalty}
                                                    >
                                                        {t('prebook.savePenalty')}
                                                    </Button>
                                                )}
                                                <Button
                                                    color="secondary"
                                                    variant="contained"
                                                    component={RouterLink}
                                                    to={createPath(paths.driversEdit, { id })}
                                                >
                                                    {t('common:edit')}
                                                </Button>
                                            </>
                                        )
                                    }
                                />
                            </>
                        )}
                        <ErrorMessage payload={getDriverPointsError} />
                        <AnchorBlock
                            id="points"
                            title={t('points.title')}
                            noValue={t('points.empty')}
                            items={points?.nb_operations > 0 ? this.renderPoints(points) : null}
                            loader={
                                !points && isLoadingDriverPoints ? (
                                    <CircularProgress
                                        size={25}
                                        style={{ display: 'flex', flex: 1, justifyContent: 'center' }}
                                    />
                                ) : null
                            }
                        />
                        <Anchor id="documents" />
                        <EnhancedListItem
                            datas={this.listDocumentsBuilder(driverPayload)}
                            title={t('common:form.documents')}
                        />
                        <SentMessages uuid={driverPayload.uuid} />
                        <ActionLogs
                            history={driverPayload.history}
                            withValues
                            //customFilter={(h) => h.action === 'ban' || h.action === 'unban'}
                        />
                    </>
                )}
            </App>
        );
    }
}

const DriverViewPageWithStyleAndTranslation = withGlobalStyles(
    withStyles(styles)(withTranslation(['driver', 'login', 'common', 'prebook', 'booking'])(DriverViewPage)),
);

const mapStateToProps = (state) => ({
    drivers: driverSelectors.drivers(state),
    driverPoints: driverSelectors.driverPoints(state),
    dispatchGroups: driverSelectors.sortedDispatchGroup(state),
    resetDriverPwdError: errorsSelectors.resetDriverPwdError(state),
    editDriverError: errorsSelectors.editDriverError(state),
    getDriverError: errorsSelectors.getDriverError(state),
    getDriverPointsError: errorsSelectors.getDriverPointsError(state),
    getDispatchGroupsError: errorsSelectors.getDispatchGroupsError(state),
    suspendDriverError: errorsSelectors.suspendDriverError(state),
    unsuspendDriverError: errorsSelectors.unsuspendDriverError(state),
    saveAdjustmentError: errorsSelectors.saveAdjustmentError(state),
    isSuspendingOrUnsuspending: pendingSelectors.isSuspendingOrUnsuspending(state),
    isLoadingDriverPoints: pendingSelectors.isLoadingDriverPoints(state),
    isEditingDriver: pendingSelectors.isPending(state, driverTypes.EDIT_DRIVER),
});

const mapDispatchToProps = {
    getDriver: driverActions.getDriver,
    getDriverPoints: driverActions.getDriverPoints,
    editDriver: driverActions.editDriver,
    resetDriverPassword: driverActions.resetDriverPassword,
    getDispatchGroups: driverActions.getDispatchGroups,
    suspendDriver: driverActions.suspendDriver,
    unsuspendDriver: driverActions.unsuspendDriver,
    saveAdjustment: partnerActions.saveAdjustment,
    clearError: errorActions.clearError,
};

export default connect(mapStateToProps, mapDispatchToProps)(DriverViewPageWithStyleAndTranslation);
